import React, { useEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import ModalCloseButton from "@/Components/ModalCloseButton"
import { MODAL_SHELL_CLASS } from "@/Components/Modal"
import {
  allHelpEntries,
  helpCategories,
  helpEntryCount,
  IHelpEntry,
} from "@/utils/helpGuide"

/**
 * The in-game guide, opened from the Help key in the top-right cluster.
 *
 * Built as a panel rather than a hover tooltip on purpose: there are ~60 things
 * on screen during a game, and a new player needs to be able to go looking for
 * the one that is confusing them rather than happening to hover it. So it wears
 * the same shell as the settings menu — category rail on the left, content on
 * the right — with a search box that cuts across every category at once.
 *
 * The game keeps running behind it. Nothing in here changes any game state, so
 * it can be opened mid-turn without cost — though the turn timer does not stop,
 * which is why the header says so.
 */

// The card for one entry: a miniature of the real thing, then what it is, where
// to find it, and the bit players get wrong.
const HelpEntryCard: React.FC<{ entry: IHelpEntry; categoryLabel?: string }> = ({
  entry,
  categoryLabel,
}) => (
  <div
    className="group relative flex gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-colors duration-150 hover:border-amber-400/30 hover:bg-white/[0.06]"
    data-testid={`game.help-entry.${entry.id}`}
  >
    {/* Preview well. Fixed width so every card's text starts on the same line,
        and centred inside it so a wide preview (a board tile pair) and a narrow
        one (a pill) both sit under the same axis. */}
    <div className="flex w-[5.5rem] shrink-0 items-center justify-center">
      <div className="pointer-events-none flex items-center justify-center">{entry.preview}</div>
    </div>

    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h4 className="font-poppins text-base font-bold leading-tight text-white">{entry.name}</h4>
        {categoryLabel && (
          <span className="rounded-full bg-amber-400/10 px-2 py-0.5 font-poppins text-[10px] font-semibold uppercase tracking-wide text-amber-300/70">
            {categoryLabel}
          </span>
        )}
      </div>

      <p className="mt-1.5 font-poppins text-[0.875rem] leading-snug text-white/70">
        {entry.description}
      </p>

      {entry.where && (
        <p className="mt-2 flex items-start gap-1.5 font-poppins text-[0.8125rem] leading-snug text-white/40">
          <i className="fa-solid fa-location-dot mt-[3px] shrink-0 text-[0.6875rem] text-white/30"></i>
          <span>{entry.where}</span>
        </p>
      )}

      {entry.tip && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-400/10 px-2 py-1.5 font-poppins text-[0.8125rem] font-medium leading-snug text-amber-200/80">
          <i className="fa-solid fa-lightbulb mt-[3px] shrink-0 text-[0.6875rem] text-amber-300"></i>
          <span>{entry.tip}</span>
        </p>
      )}
    </div>
  </div>
)

const HelpModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const visible = useAppSelector((state) => state.global.modalData.help?.visible)
  const [activeCategoryId, setActiveCategoryId] = useState(helpCategories[0].id)
  const [query, setQuery] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleClose = () => {
    dispatch(setModalData({ help: { visible: false } }))
  }

  const searching = query.trim().length > 0

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return []
    // Every word has to appear somewhere in the entry, so "trade button" finds
    // Create Trade rather than everything mentioning either word.
    const words = needle.split(/\s+/)
    return allHelpEntries.filter((entry) => {
      const haystack = [
        entry.name,
        entry.description,
        entry.where ?? "",
        entry.tip ?? "",
        entry.keywords ?? "",
        entry.categoryLabel,
      ]
        .join(" ")
        .toLowerCase()
      return words.every((word) => haystack.includes(word))
    })
  }, [query])

  const activeCategory =
    helpCategories.find((category) => category.id === activeCategoryId) ?? helpCategories[0]

  // Esc closes. Registered while open only, so it doesn't fight the other
  // modals' own Esc handlers when this one isn't up.
  useEffect(() => {
    if (!visible) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        handleClose()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [visible])

  // Opening lands the caret in the search box — the fastest route to a specific
  // thing is almost always typing its name.
  useEffect(() => {
    if (!visible) return
    const timeout = setTimeout(() => searchRef.current?.focus(), 60)
    return () => clearTimeout(timeout)
  }, [visible])

  // Switching category (or starting a search) starts the reading pane at the
  // top instead of inheriting the previous section's scroll position.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [activeCategoryId, searching])

  if (!visible) return null

  return (
    <div
      className="settings-overlay-enter fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={handleClose}
      data-testid="game.help-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Game guide"
        className={classNames(
          MODAL_SHELL_CLASS,
          // overflow-hidden matters: without it the rail's tint and border spill
          // past the panel's rounded corners.
          "settings-panel-enter relative flex h-[86vh] w-full max-w-6xl overflow-hidden sm:h-[94vh]"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left rail — brand, categories, and the count pinned at the bottom. */}
        <aside className="flex w-60 shrink-0 flex-col border-r border-white/10 bg-black/40 sm:w-48">
          <div className="flex items-center gap-3 px-4 pb-4 pt-5 sm:pb-3 sm:pt-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 shadow-lg shadow-sky-500/20 sm:h-9 sm:w-9">
              <i className="fa-solid fa-circle-question text-base text-black/80"></i>
            </span>
            <div className="min-w-0">
              <h2 className="font-poppins text-base font-bold tracking-wide text-white">
                Game Guide
              </h2>
              <p className="truncate font-poppins text-[11px] text-white/40">
                Every button, explained
              </p>
            </div>
          </div>

          <nav
            aria-label="Guide sections"
            className="challenge-scroll flex flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3"
          >
            {helpCategories.map((category) => {
              const isActive = !searching && activeCategoryId === category.id
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => {
                    setQuery("")
                    setActiveCategoryId(category.id)
                  }}
                  data-testid={`game.help-section.${category.id}`}
                  aria-current={isActive ? "page" : undefined}
                  className={classNames(
                    "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors sm:py-2",
                    isActive
                      ? "bg-amber-500/20 text-amber-100 ring-1 ring-amber-400/40"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  )}
                >
                  {/* Accent bar on the active row — reads as "you are here" even
                      when the amber tint is hard to see. */}
                  <span
                    className={classNames(
                      "absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-amber-400 transition-opacity",
                      isActive ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <i
                    className={classNames(
                      "fa-solid w-5 shrink-0 text-center text-sm transition-colors",
                      category.icon,
                      isActive ? "text-amber-300" : "text-white/40 group-hover:text-white/70"
                    )}
                  ></i>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-poppins text-sm font-semibold">
                      {category.label}
                    </span>
                    {/* Dropped on short screens, where the rail needs the height
                        more than it needs the extra line. */}
                    <span
                      className={classNames(
                        "block truncate font-poppins text-[11px] sm:hidden",
                        isActive ? "text-amber-200/70" : "text-white/40"
                      )}
                    >
                      {category.hint}
                    </span>
                  </span>
                  <span
                    className={classNames(
                      "shrink-0 font-poppins text-[10px] font-bold tabular-nums",
                      isActive ? "text-amber-200/60" : "text-white/25"
                    )}
                  >
                    {category.entries.length}
                  </span>
                </button>
              )
            })}
          </nav>

          <div className="border-t border-white/10 px-4 py-3 sm:hidden">
            <p className="font-poppins text-[11px] leading-relaxed text-white/30">
              {helpEntryCount} things explained.
              <br />
              <kbd className="mt-1 inline-block rounded border border-white/20 bg-white/5 px-1.5 py-0.5 font-poppins text-[10px] font-semibold text-white/50">
                Esc
              </kbd>{" "}
              to close
            </p>
          </div>
        </aside>

        {/* Reading pane */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-6 py-4 sm:px-4 sm:py-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/30 sm:hidden">
                <i
                  className={classNames(
                    "fa-solid text-sm",
                    searching ? "fa-magnifying-glass" : activeCategory.icon
                  )}
                ></i>
              </span>
              <div className="min-w-0">
                <h3 className="truncate font-poppins text-xl font-bold text-white sm:text-lg">
                  {searching
                    ? `${results.length} ${results.length === 1 ? "match" : "matches"}`
                    : activeCategory.label}
                </h3>
                <p className="truncate font-poppins text-xs text-white/40">
                  {searching ? `Searching for “${query.trim()}”` : activeCategory.blurb}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              {/* Search cuts across every category at once, so a player who
                  knows what the thing is called never has to guess which
                  section it was filed under. */}
              <div className="relative hidden md:block">
                <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-white/30"></i>
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search the guide…"
                  aria-label="Search the guide"
                  data-testid="game.help-search"
                  className="w-56 rounded-lg border border-white/10 bg-white/5 py-2 pl-8 pr-8 font-poppins text-sm text-white placeholder:text-white/30 focus:border-amber-400/40 focus:outline-none focus:ring-1 focus:ring-amber-400/30"
                />
                {searching && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("")
                      searchRef.current?.focus()
                    }}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-white/30 transition-colors hover:text-white/70"
                  >
                    <i className="fa-solid fa-xmark text-xs"></i>
                  </button>
                )}
              </div>
              <ModalCloseButton onClick={handleClose} data-testid="game.help-close" />
            </div>
          </div>

          {/* Search box for narrow layouts, where it doesn't fit in the header.
              Deliberately outside the keyed region below: that region remounts
              the moment a search starts, which would take the caret out of an
              input living inside it on the very first keystroke. */}
          <div className="relative shrink-0 px-6 pt-4 sm:px-4 sm:pt-3 md:hidden">
            <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-9 top-1/2 -translate-y-1/2 text-xs text-white/30 sm:left-7"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the guide…"
              aria-label="Search the guide"
              className="w-full rounded-lg border border-white/10 bg-white/5 py-2 pl-8 pr-3 font-poppins text-sm text-white placeholder:text-white/30 focus:border-amber-400/40 focus:outline-none focus:ring-1 focus:ring-amber-400/30"
            />
          </div>

          {/* Keyed on what is being shown so switching sections replays the
              entrance rather than swapping content in place. */}
          <div
            ref={scrollRef}
            key={searching ? "search" : activeCategory.id}
            className="settings-section-enter challenge-scroll flex-1 overflow-y-auto px-6 py-5 sm:px-4 sm:py-4"
          >
            {searching ? (
              results.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                  {results.map((entry) => (
                    <HelpEntryCard
                      key={`${entry.categoryId}.${entry.id}`}
                      entry={entry}
                      categoryLabel={entry.categoryLabel}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                  <i className="fa-solid fa-magnifying-glass text-2xl text-white/20"></i>
                  <p className="font-poppins text-sm text-white/40">
                    Nothing in the guide matches “{query.trim()}”.
                  </p>
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="font-poppins text-sm font-semibold text-amber-300/80 underline-offset-4 hover:underline"
                  >
                    Clear the search
                  </button>
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                {activeCategory.entries.map((entry) => (
                  <HelpEntryCard key={entry.id} entry={entry} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HelpModal
